import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { focusables, overlayStack, tabKeyIndex, wrapTab } from "./dom.js";
import { Button } from "./primitives.js";

/**
 * Modal / Drawer behaviour: on open, focus moves to the first control in the panel (not the close button) or the
 * panel itself, and Tab stays inside it; Escape and the backdrop act on the top-most overlay only, so a dialog opened
 * from a drawer closes alone; on close, focus returns to whatever opened it.
 */
function useOverlay<T extends HTMLElement>(open: boolean, onClose: () => void) {
  const panel = useRef<T>(null);
  const close = useRef(onClose);
  const [self] = useState(() => ({}));
  useEffect(() => { close.current = onClose; });
  // The opener is taken while rendering the open state, before an autoFocus child moves focus into the panel.
  const opener = useRef<Element | null>(null);
  const wasOpen = useRef(false);
  if (open !== wasOpen.current) {
    wasOpen.current = open;
    if (open && typeof document !== "undefined") opener.current = document.activeElement;
  }

  useEffect(() => {
    const el = panel.current;
    if (!open || !el) return;
    overlayStack.push(self);
    if (!el.contains(document.activeElement)) (focusables(el).find((x) => !x.classList.contains("tos-close")) ?? el).focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || !overlayStack.isTop(self)) return;
      if (e.key === "Escape") close.current();
      else if (e.key === "Tab") {
        const items = focusables(el);
        const next = items.length ? wrapTab(items, document.activeElement as HTMLElement | null, e.shiftKey) : el;
        if (next) { e.preventDefault(); next.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    const back = opener.current;
    return () => {
      document.removeEventListener("keydown", onKey);
      overlayStack.remove(self);
      if (back instanceof HTMLElement && back.isConnected) back.focus();
    };
  }, [open, self]);

  const onBackdrop = (e: { target: EventTarget; currentTarget: EventTarget }) => { if (e.target === e.currentTarget && overlayStack.isTop(self)) close.current(); };
  return { panel, onBackdrop };
}

export function Modal({ open, title, onClose, children, footer, wide }: { open: boolean; title: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const { panel, onBackdrop } = useOverlay<HTMLDivElement>(open, onClose);
  const titleId = useId();
  if (!open) return null;
  return (
    <div className="tos-overlay tos-modal-overlay" onMouseDown={onBackdrop}>
      <div ref={panel} tabIndex={-1} className={`tos-modal${wide ? " wide" : ""}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="tos-modal-head">
          <h2 id={titleId} className="tos-modal-title">{title}</h2>
          <button type="button" className="tos-close" onClick={onClose} aria-label="Close">×</button>
        </header>
        <div className="tos-modal-body">{children}</div>
        {footer && <footer className="tos-modal-foot">{footer}</footer>}
      </div>
    </div>
  );
}

export function Drawer({ open, title, onClose, children, footer }: { open: boolean; title: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  const { panel, onBackdrop } = useOverlay<HTMLElement>(open, onClose);
  const titleId = useId();
  if (!open) return null;
  return (
    <>
      <div className="tos-overlay" onMouseDown={onBackdrop} />
      <aside ref={panel} tabIndex={-1} className="tos-drawer" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="tos-modal-head">
          <h2 id={titleId} className="tos-modal-title">{title}</h2>
          <button type="button" className="tos-close" onClick={onClose} aria-label="Close">×</button>
        </header>
        <div className="tos-modal-body" style={{ flex: 1 }}>{children}</div>
        {footer && <footer className="tos-modal-foot">{footer}</footer>}
      </aside>
    </>
  );
}

// ---- toasts
type ToastTone = "neutral" | "success" | "danger";
interface ToastItem { id: number; message: string; tone: ToastTone }
const ToastCtx = createContext<(message: string, tone?: ToastTone) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((message: string, tone: ToastTone = "neutral") => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs, { id, message, tone }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), tone === "danger" ? 7000 : 3500);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="tos-toasts" aria-live="polite">
        {items.map((t) => <div key={t.id} className={`tos-toast${t.tone !== "neutral" ? ` tone-${t.tone}` : ""}`}>{t.message}</div>)}
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);

// ---- confirm dialog: const confirm = useConfirm(); if (await confirm({ title, message })) ...
interface ConfirmOpts { title: string; message?: ReactNode; confirmLabel?: string; danger?: boolean }
const ConfirmCtx = createContext<(o: ConfirmOpts) => Promise<boolean>>(async () => false);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<(ConfirmOpts & { resolve: (v: boolean) => void }) | null>(null);
  const confirm = useCallback((o: ConfirmOpts) => new Promise<boolean>((resolve) => setState({ ...o, resolve })), []);
  const close = (v: boolean) => { state?.resolve(v); setState(null); };
  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <Modal open={Boolean(state)} title={state?.title ?? ""} onClose={() => close(false)}
        footer={<><Button onClick={() => close(false)}>Cancel</Button><Button variant={state?.danger ? "danger" : "primary"} onClick={() => close(true)}>{state?.confirmLabel ?? "Confirm"}</Button></>}>
        {state?.message}
      </Modal>
    </ConfirmCtx.Provider>
  );
}
export const useConfirm = () => useContext(ConfirmCtx);

export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { id: T; label: ReactNode }[]; value: T; onChange: (id: T) => void }) {
  return (
    <div className="tos-tabs" role="tablist">
      {tabs.map((t, index) => (
        <button key={t.id} type="button" role="tab" className="tos-tab" aria-selected={t.id === value} tabIndex={t.id === value ? 0 : -1}
          onClick={() => onChange(t.id)} onKeyDown={(e) => {
            const next = tabKeyIndex(e.key, index, tabs.length);
            if (next === null) return;
            e.preventDefault();
            e.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>("[role=tab]")[next]?.focus();
            onChange(tabs[next]!.id);
          }}>{t.label}</button>
      ))}
    </div>
  );
}
